import {
  getGuardrailDecisionDisplay,
  type ApprovalGroup,
  type ApprovalStep,
  type GuardrailCheck,
  type GuardrailDecision,
  type GuardrailEvaluation,
  type GuardrailResult,
} from "src/components/RequisitionGuardrailOutcome";
import {
  withRuleLabels,
  type RuleLabel,
} from "src/components/guardrailRuleLabels";

import type { InboxItemBase } from "../item";
import { parseInboxItemKey, type InboxItemKind } from "../kinds";
import type { InboxRow } from "../row";

import { failingCheckSentences } from "./failingCheckLabels";

export { failingCheckLabels } from "./failingCheckLabels";
export { personLabel } from "./personLabel";

export type InboxPerson = {
  aggregateId?: string | null;
  firstName?: string | null;
  surname?: string | null;
  preferredName?: string | null;
};

export type InboxRequisition = GuardrailResult & {
  externalId?: string | null;
  state?: string | null;
  positionId?: string | null;
  role?: string | null;
  fte?: number | null;
  location?: string | null;
  openedDate?: string | null;
  targetStartDate?: string | null;
  justification?: string | null;
  supplyTeam?: { id?: string | null; name?: string | null } | null;
  hiringManager?: InboxPerson | null;
};

/** Card payload. Detail fetches `InboxRequisition` via lookup. */
type InboxListRequisition = {
  id?: string | null;
  externalId?: string | null;
  role?: string | null;
  fte?: number | null;
  supplyTeam?: { id?: string | null; name?: string | null } | null;
  guardrailEvaluation?: { checks?: GuardrailCheck[] | null } | null;
};

type RequisitionInboxShared = InboxItemBase & {
  title: string;
  subtitle: string;
  failingChecks: string[];
  requisitionId: string;
  order: number;
};

export type RequisitionApprovalItem = RequisitionInboxShared & {
  kind: "REQUISITION_APPROVAL";
  approverGroupId: string;
  elapsedHours: number | null;
  clockStartedAt: string | null;
};

export type RequisitionExceptionReasonItem = RequisitionInboxShared & {
  kind: "REQUISITION_EXCEPTION_REASON";
};

export type RequisitionInboxItem =
  | RequisitionApprovalItem
  | RequisitionExceptionReasonItem;

type RequisitionInboxRow = InboxRow & {
  order?: number | null;
  approverGroupId?: string | null;
  approverGroupName?: string | null;
  elapsedHours?: number | null;
  clockStartedAt?: string | null;
  requisition?: InboxListRequisition | null;
};

const text = (value: string | null | undefined): string => value?.trim() ?? "";

const fteLabel = (fte: number | null | undefined): string => {
  if (typeof fte !== "number" || !Number.isFinite(fte)) {
    return "";
  }
  return `${fte} FTE`;
};

export const labelInboxRequisition = (
  requisition: InboxRequisition,
  rules: RuleLabel[]
): InboxRequisition => {
  const evaluation = requisition.guardrailEvaluation;
  if (!evaluation) {
    return requisition;
  }
  const next = withRuleLabels(evaluation, rules);
  if (!next || next === evaluation) {
    return requisition;
  }
  return { ...requisition, guardrailEvaluation: next };
};

const approvalGroups = (
  steps: ApprovalStep[] | null | undefined
): ApprovalGroup[] =>
  (steps ?? []).flatMap((step) =>
    step.kind === "group" ? (step.groups ?? []) : []
  );

const isEmptyReasonStep = (step: ApprovalStep): boolean =>
  step.kind === "reason" && step.status === "PENDING";

export const approvalStatus = (
  steps: ApprovalStep[] | null | undefined,
  decision?: GuardrailDecision | null,
  template?: GuardrailEvaluation["template"]
): {
  label: string;
  variant: "warning" | "success" | "danger" | "primary";
} => {
  if ((steps ?? []).some(isEmptyReasonStep)) {
    return { label: "Waiting for your reason", variant: "warning" };
  }
  const groups = approvalGroups(steps);
  if (groups.length === 0) {
    const display = getGuardrailDecisionDisplay(decision, template);
    return display ?? { label: "In approval", variant: "warning" };
  }
  if (
    groups.some(
      (group) => group.status === "PENDING" || text(group.status) === ""
    )
  ) {
    return { label: "In approval", variant: "warning" };
  }
  if (groups.some((group) => group.status === "REFUSED")) {
    return { label: "Refused", variant: "danger" };
  }
  if (groups.some((group) => group.status === "APPROVED")) {
    return { label: "Approved", variant: "success" };
  }
  return { label: "In approval", variant: "warning" };
};

export const decisionTarget = (
  steps: ApprovalStep[] | null | undefined,
  approverGroupId: string,
  decision: string | null | undefined
): { order: number; group: ApprovalGroup } | null => {
  if (decision !== "PASS" || text(approverGroupId) === "") {
    return null;
  }
  const ordered = [...(steps ?? [])].sort(
    (left, right) => left.order - right.order
  );
  for (const step of ordered) {
    if (step.kind !== "group") {
      continue;
    }
    for (const group of step.groups ?? []) {
      if (
        group.approverGroupId === approverGroupId &&
        group.canRespond === true
      ) {
        return { order: step.order, group };
      }
    }
  }
  return null;
};

export const pillOf = (
  kind: Extract<
    InboxItemKind,
    "REQUISITION_APPROVAL" | "REQUISITION_EXCEPTION_REASON"
  >
): { label: string; variant: "primaryLight" | "warning" } => {
  switch (kind) {
    case "REQUISITION_APPROVAL":
      return { label: "Approval", variant: "primaryLight" };
    case "REQUISITION_EXCEPTION_REASON":
      return { label: "Exception reason", variant: "warning" };
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};

export const stepLineOf = (item: RequisitionInboxItem): string => {
  switch (item.kind) {
    case "REQUISITION_APPROVAL":
      return `Step: ${item.owedAs}`;
    case "REQUISITION_EXCEPTION_REASON":
      return item.owedAs;
    default: {
      const unexpected: never = item;
      return unexpected;
    }
  }
};

const finiteOrNull = (value: number | null | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** Id beside the inbox kind badge: external id, otherwise the requisition id. */
export const inboxRequisitionHeaderId = (
  requisition:
    | { id?: string | null; externalId?: string | null }
    | null
    | undefined
): string => text(requisition?.externalId) || text(requisition?.id);

const titleOf = (
  row: RequisitionInboxRow,
  requisition: InboxListRequisition | null
): string =>
  text(requisition?.role) ||
  text(requisition?.externalId) ||
  text(parseInboxItemKey(row.id)?.subjectId) ||
  "Requisition";

const subtitleOf = (requisition: InboxListRequisition | null): string =>
  [text(requisition?.supplyTeam?.name), fteLabel(requisition?.fte)]
    .filter(Boolean)
    .join(" · ");

const sharedOf = (
  row: RequisitionInboxRow,
  requisition: InboxListRequisition | null
): Omit<RequisitionInboxShared, "kind" | "slaHours" | "overdue" | "owedAs"> => {
  const requisitionId =
    text(parseInboxItemKey(row.id)?.subjectId) ||
    text(requisition?.id) ||
    row.id;
  return {
    key: row.id,
    since: row.since ?? null,
    title: titleOf(row, requisition),
    subtitle: subtitleOf(requisition),
    failingChecks: failingCheckSentences(
      requisition?.guardrailEvaluation?.checks
    ),
    requisitionId,
    order: typeof row.order === "number" ? row.order : 0,
  };
};

const approvalOwedAs = (row: RequisitionInboxRow) =>
  text(row.owedAs) ||
  `Approving as ${text(row.approverGroupName) || "Approver group"}`;

export const parseRequisitionInboxRow = (
  row: InboxRow
): RequisitionInboxItem | null => {
  const typed = row as RequisitionInboxRow;
  const requisition = typed.requisition ?? null;
  const shared = sharedOf(typed, requisition);
  switch (row.kind) {
    case "REQUISITION_APPROVAL": {
      const approverGroupId = text(typed.approverGroupId);
      return {
        ...shared,
        kind: "REQUISITION_APPROVAL",
        owedAs: approvalOwedAs(typed),
        slaHours: finiteOrNull(typed.slaHours),
        overdue: typed.overdue === true,
        approverGroupId,
        elapsedHours: finiteOrNull(typed.elapsedHours),
        clockStartedAt: typed.clockStartedAt ?? null,
      };
    }
    case "REQUISITION_EXCEPTION_REASON":
      return {
        ...shared,
        kind: "REQUISITION_EXCEPTION_REASON",
        owedAs:
          text(typed.owedAs) ||
          "You raised this · reason needed before approval starts",
        slaHours: null,
        overdue: false,
      };
    default:
      return null;
  }
};

/** After a reason save, jump to step-1 approval for the same requisition. */
export const afterRequisitionRemoval = (
  removed: RequisitionInboxItem,
  remaining: readonly RequisitionInboxItem[]
): string | null => {
  if (removed.kind !== "REQUISITION_EXCEPTION_REASON") {
    return null;
  }
  const next = remaining.find(
    (item) =>
      item.kind === "REQUISITION_APPROVAL" &&
      item.requisitionId === removed.requisitionId
  );
  return next?.key ?? null;
};

const stripPendingReason = (step: ApprovalStep): ApprovalStep => {
  if (step.kind !== "reason" || step.status !== "PENDING") {
    return step;
  }
  const { status, ...rest } = step;
  return rest;
};

const readOnlySteps = (
  steps: ApprovalStep[] | null | undefined
): ApprovalStep[] =>
  (steps ?? []).map((step) => {
    const withoutPendingReason = stripPendingReason(step);
    const groups = withoutPendingReason.groups;
    if (!groups) {
      return withoutPendingReason;
    }
    return {
      ...withoutPendingReason,
      groups: groups.map((group) => ({
        ...group,
        canRespond: false,
        canUndo: false,
      })),
    };
  });

export const readOnlyRequisition = (
  requisition: InboxRequisition
): InboxRequisition => {
  const evaluation = requisition.guardrailEvaluation;
  if (!evaluation) {
    return requisition;
  }
  return {
    ...requisition,
    guardrailEvaluation: {
      ...evaluation,
      steps: readOnlySteps(evaluation.steps),
    },
  };
};
